<script setup>
import { computed } from 'vue'
import {
  ArrowRight,
  CalendarDays,
  ChartNoAxesColumnIncreasing,
  ChevronRight,
  CircleCheckBig,
  Clock3,
  Flame,
  Play,
  TrendingDown,
} from 'lucide-vue-next'
import Button from 'primevue/button'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { createDashboardOverviewResource } from '@/services/learning'
import { formatDate } from '@/utils/datetimeFormat'

const router = useRouter()
const { t } = useI18n()
const dashboardResource = createDashboardOverviewResource()
const overview = computed(() => dashboardResource.data || {})
const reviewQueue = computed(() => overview.value.review_preview || [])
const focusAreas = computed(() => overview.value.needs_attention || [])
const continueLearning = computed(() => overview.value.continue_learning)
const plan = computed(() => overview.value.today || {})
const metrics = computed(() => overview.value.metrics || {})
const recallTimeline = computed(() => overview.value.recall_performance?.timeline || [])
const maxRecallActivity = computed(() =>
  Math.max(1, ...recallTimeline.value.map((day) => (day.remembered || 0) + (day.forgot || 0))),
)
const dateLabel = computed(() =>
  new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
    .format(new Date())
    .toUpperCase(),
)
const displayName = computed(() => overview.value.user?.display_name || t('dashboard.there'))
const formatValue = (value) => (value === null || value === undefined ? '—' : value)
const formatMinutes = (value) => (value == null ? '—' : t('dashboard.minutesShort', { count: value }))
const formatRecallDate = (value) => formatDate(value) || value
function reviewState(item) {
  return item.status === 'Overdue' ? 'danger' : 'primary'
}
function reviewStatus(status) {
  const key = { Overdue: 'overdue', 'Due today': 'dueToday', 'Due now': 'dueNow' }[status]
  return t(`dashboard.reviewStatus.${key || 'unknown'}`, status)
}
function focusLabel(label) {
  const key = label === 'Repeated recall failure' ? 'repeatedRecallFailure' : null
  return t(`dashboard.focusLabels.${key || 'needsReview'}`, label)
}
function openLearning(id) {
  if (id) router.push({ name: 'learning-detail', params: { learningId: id } })
}
function openReview(learningId) {
  if (learningId) router.push({ name: 'review', query: { learningId } })
}
function focusAttention(item) {
  if (!item?.learning_id) return
  if (item.action === 'practice') {
    router.push({ name: 'active-recall', params: { learningId: item.learning_id }, query: { mode: 'practice' } })
    return
  }
  openReview(item.learning_id)
}
function startSession() {
  router.push(
    plan.value.due_reviews
      ? { name: 'review' }
      : continueLearning.value
        ? { name: 'learning-detail', params: { learningId: continueLearning.value.name } }
        : { name: 'learn' },
  )
}
</script>

<template>
  <div class="dashboard-page">
    <section
      v-if="dashboardResource.loading"
      class="dashboard-state"
    >
      <span class="loading-spinner" />
      {{ $t('dashboard.loadingOverview') }}
    </section>
    <section
      v-else-if="dashboardResource.error"
      class="dashboard-state"
    >
      <h2>{{ $t('dashboard.overviewErrorTitle') }}</h2>
      <p>{{ $t('dashboard.overviewErrorMessage') }}</p>
      <Button
        type="button"
        class="start-session-button"
        unstyled
        @click="dashboardResource.reload()"
      >
        {{ $t('common.tryAgain') }}
      </Button>
    </section>
    <!-- =========================
         DESKTOP DASHBOARD
    ========================== -->

    <section
      v-else
      class="desktop-dashboard"
    >
      <div class="desktop-title-row">
        <div>
          <div class="eyebrow">{{ dateLabel }}</div>

          <h1>
            {{ $t('dashboard.greeting', { name: displayName }) }}
            <br />
            {{ $t('dashboard.todaysFocus') }}
          </h1>
        </div>

        <Button
          type="button"
          v-if="continueLearning"
          class="continue-button"
          unstyled
          @click="openLearning(continueLearning.name)"
        >
          <Play
            :size="18"
            fill="none"
          />
          {{ $t('common.continueLearning') }}: {{ continueLearning.content?.title }}
        </Button>
      </div>

      <div class="dashboard-top-grid">
        <!-- Today's plan -->
        <section class="card today-plan">
          <div class="card-title-row">
            <h2>{{ $t('dashboard.todaysPlan') }}</h2>

            <CalendarDays
              class="primary-icon"
              :size="23"
            />
          </div>

          <div class="plan-stats">
            <div class="plan-stat">
              <strong>{{ formatValue(plan.new_items) }}</strong>
              <span>{{ $t('dashboard.newTopics') }}</span>
            </div>

            <div class="plan-stat">
              <strong>{{ formatValue(plan.due_reviews) }}</strong>
              <span>{{ $t('dashboard.reviewsDue') }}</span>
            </div>
          </div>

          <div class="estimated-time">
            <div class="estimated-label">
              <Clock3 :size="21" />
              <span>{{ $t('dashboard.estimatedTime') }}</span>
            </div>

            <strong>{{ formatMinutes(plan.estimated_minutes) }}</strong>
          </div>

          <div class="daily-goal">
            <div class="daily-goal-label">
              <span>{{ $t('dashboard.dailyGoal') }}</span>
              <span>—</span>
            </div>

            <div class="progress-track">
              <div
                class="progress-value"
                style="width: 0%"
              />
            </div>
          </div>

          <Button
            type="button"
            class="start-session-button"
            unstyled
            @click="startSession"
          >
            <span>
              {{ plan.due_reviews ? $t('dashboard.startReview') : continueLearning ? $t('common.continueLearning') : $t('dashboard.openLearn') }}
            </span>
            <ArrowRight :size="23" />
          </Button>
        </section>

        <div class="desktop-right-column">
          <!-- Stats -->
          <div class="stats-row">
            <div class="card stat-card">
              <div class="stat-label">
                <Flame
                  :size="21"
                  class="primary-icon"
                />
                {{ $t('dashboard.streak') }}
              </div>

              <div class="stat-value">
                <strong>{{ formatValue(metrics.streak_days) }}</strong>
                <span>{{ $t('dashboard.days') }}</span>
              </div>
            </div>

            <div class="card stat-card">
              <div class="stat-label">
                <CircleCheckBig
                  :size="21"
                  class="primary-icon"
                />
                {{ $t('dashboard.mastered') }}
              </div>

              <div class="stat-value">
                <strong>{{ formatValue(metrics.mastered) }}</strong>
                <span>{{ $t('dashboard.thisWeek') }}</span>
              </div>
            </div>

            <div class="card stat-card">
              <div class="stat-label">
                <ChartNoAxesColumnIncreasing
                  :size="21"
                  class="primary-icon"
                />
                {{ $t('dashboard.retention') }}
              </div>

              <div class="stat-value">
                <strong>{{ metrics.retention == null ? '—' : `${metrics.retention}%` }}</strong>
                <span>{{ $t('dashboard.average') }}</span>
              </div>
            </div>
          </div>

          <!-- Review queue -->
          <section class="card review-card">
            <h2>{{ $t('dashboard.reviewQueue') }}</h2>

            <div class="review-list">
              <Button
                v-for="item in reviewQueue"
                :key="item.knowledge_item"
                type="button"
                class="review-item"
                :class="`review-item--${reviewState(item)}`"
                unstyled
                @click="openReview(item.learning_id)"
              >
                <span
                  class="review-dot"
                  :class="`review-dot--${reviewState(item)}`"
                />

                <span class="review-content">
                  <strong>{{ item.title }}</strong>
                  <small>{{ reviewStatus(item.status) }}</small>
                </span>
              </Button>
            </div>
          </section>
        </div>
      </div>

      <!-- Bottom desktop -->
      <div class="dashboard-bottom-grid">
        <section class="card recall-card">
          <div class="card-title-row">
            <div>
              <h2>{{ $t('dashboard.recallPerformance') }}</h2>
              <span class="section-subtitle">{{ $t('dashboard.pastSevenDays') }}</span>
            </div>

            <Button
              type="button"
              class="topic-filter"
              unstyled
            >
              {{ $t('dashboard.allTopics') }}
              <ChevronRight
                :size="15"
                class="filter-chevron"
              />
            </Button>
          </div>

          <div class="chart">
            <div
              v-if="!recallTimeline.some((day) => day.remembered || day.forgot)"
              class="chart-empty"
            >
              {{ $t('dashboard.notEnoughRecallData') }}
            </div>
            <div
              v-else
              class="recall-chart"
              role="img"
              :aria-label="$t('dashboard.recallAttemptsAriaLabel')"
            >
              <div
                v-for="day in recallTimeline"
                :key="day.date"
                class="recall-chart-day"
                :title="$t('dashboard.recallAttemptsTooltip', { date: formatRecallDate(day.date), count: (day.remembered || 0) + (day.forgot || 0) })"
              >
                <div
                  class="recall-chart-bar"
                  :style="{
                    height: `${Math.max(2, (((day.remembered || 0) + (day.forgot || 0)) / maxRecallActivity) * 100)}%`,
                  }"
                />
              </div>
            </div>
          </div>
        </section>

        <section class="card attention-card">
          <h2>{{ $t('dashboard.needsAttention') }}</h2>

          <div
            v-for="item in focusAreas"
            :key="item.knowledge_item"
            class="attention-item"
          >
            <div class="attention-heading">
              <div>
                <strong>{{ item.title }}</strong>

                <div class="attention-danger">
                  <TrendingDown
                    v-if="item.label === 'Repeated recall failure'"
                    :size="14"
                  />
                  {{ focusLabel(item.label) }}
                </div>
              </div>

              <div class="score-circle">!</div>
            </div>

            <div class="attention-actions">
              <Button
                type="button"
                unstyled
                @click="focusAttention(item)"
              >
                {{ item.action === 'practice' ? $t('dashboard.practiceAgain') : $t('dashboard.reviewNow') }}
              </Button>
            </div>
          </div>
          <p
            v-if="!focusAreas.length"
            class="attention-empty"
          >
            {{ $t('dashboard.nothingNeedsAttention') }}
          </p>
        </section>
      </div>
    </section>

    <!-- =========================
         MOBILE DASHBOARD
    ========================== -->

    <section class="mobile-dashboard">
      <div class="mobile-welcome">
        <div class="mobile-date">
          {{ dateLabel }}
        </div>

        <h1>{{ $t('dashboard.greetingShort', { name: displayName }) }}</h1>
      </div>

      <!-- Study session -->
      <section class="study-session-card">
        <div class="study-pattern" />

        <div class="study-card-content">
          <div>
            <h2>{{ $t('dashboard.startSession') }}</h2>
            <p>{{ plan.due_reviews || 0 }} {{ $t('dashboard.reviewsDue') }}</p>
          </div>

          <div class="study-card-footer">
            <div class="study-time">
              <Clock3 :size="16" />
              {{ formatMinutes(plan.estimated_minutes) }}
            </div>

            <Button
              type="button"
              class="study-play-button"
              unstyled
              @click="startSession"
            >
              <Play
                :size="20"
                fill="none"
              />
            </Button>
          </div>
        </div>
      </section>

      <!-- Mobile stats -->
      <div class="mobile-stats">
        <div class="mobile-stat-card">
          <div class="circle-progress">
            <ProgressCircleIcon />
            <span>{{ (plan.new_items || 0) + (plan.due_reviews || 0) }}</span>
          </div>

          <small>{{ $t('dashboard.tasksToday') }}</small>
        </div>

        <div class="mobile-stat-card streak-card">
          <div class="streak-circle">
            <Flame :size="27" :stroke-width="2" />
          </div>

          <strong>{{ formatValue(metrics.streak_days) }} {{ $t('dashboard.days') }}</strong>
          <small>{{ $t('dashboard.currentStreak') }}</small>
        </div>
      </div>

      <!-- Review queue mobile -->
      <section class="mobile-section">
        <div class="mobile-section-heading">
          <h2>{{ $t('dashboard.reviewQueue') }}</h2>

          <Button
            type="button"
            unstyled
            @click="openReview()"
          >
            {{ $t('dashboard.viewAll') }}
          </Button>
        </div>

        <div class="mobile-review-list">
          <Button
            v-for="item in reviewQueue"
            :key="item.knowledge_item"
            type="button"
            class="mobile-review-item"
            unstyled
            @click="openReview(item.learning_id)"
          >
            <span
              class="review-dot"
              :class="`review-dot--${reviewState(item)}`"
            />

            <span class="review-content">
              <strong>{{ item.title }}</strong>
              <small>{{ reviewStatus(item.status) }}</small>
            </span>

            <ChevronRight
              :size="21"
              class="review-arrow"
            />
          </Button>
        </div>
      </section>

      <!-- Focus areas -->
      <section class="mobile-section focus-section">
        <h2>{{ $t('dashboard.focusAreas') }}</h2>

        <div class="focus-grid">
          <article
            v-for="item in focusAreas"
            :key="item.knowledge_item"
            class="focus-card"
          >
            <span
              class="focus-tag"
              :class="{ 'focus-tag--danger': item.label === 'Repeated recall failure' }"
            >
              {{ focusLabel(item.label) }}
            </span>

            <strong>{{ item.title }}</strong>
            <small>{{ $t('dashboard.needsReview') }}</small>
            <Button
              type="button"
              class="focus-action"
              unstyled
              @click="focusAttention(item)"
            >
              {{ item.action === 'practice' ? $t('dashboard.practiceAgain') : $t('dashboard.reviewNow') }}
            </Button>
          </article>
        </div>
      </section>
    </section>
  </div>
</template>

<style scoped>
.dashboard-page {
  --primary: #443bd0;
  --primary-2: #5c57df;
  --primary-soft: #efefff;

  --text: #1f2022;
  --text-secondary: #555563;

  --border: #cecedf;
  --background: #fbfbfd;
  --card: #ffffff;

  background: var(--background);
  color: var(--text);
}

:global(html[data-theme='dark'] .dashboard-page) {
  --primary: #aaa6ed;
  --primary-2: #c0bdff;
  --primary-soft: #302d4d;
  --text: #f5f3f7;
  --text-secondary: #b8b4c3;
  --border: #3a3749;
  --background: #17161f;
  --card: #211f2b;
}

:global(html[data-theme='dark'] .dashboard-page .plan-stat),
:global(html[data-theme='dark'] .dashboard-page .estimated-time),
:global(html[data-theme='dark'] .dashboard-page .topic-filter),
:global(html[data-theme='dark'] .dashboard-page .attention-actions button),
:global(html[data-theme='dark'] .dashboard-page .streak-circle),
:global(html[data-theme='dark'] .dashboard-page .focus-card) {
  background: #2b2937;
  border-color: #4a465a;
}

:global(html[data-theme='dark'] .dashboard-page .review-item),
:global(html[data-theme='dark'] .dashboard-page .mobile-review-item) {
  color: #f5f3f7;
  background: #2b2937;
  border-color: #4a465a;
}

:global(html[data-theme='dark'] .dashboard-page .review-item--danger) {
  background: #38282e;
  border-color: #754451;
}

:global(html[data-theme='dark'] .dashboard-page) :is(
  .plan-stat span, .estimated-label, .daily-goal-label, .stat-value span,
  .review-content small,
  .section-subtitle, .chart-empty, .chart-label, .mobile-date,
  .mobile-stat-card small, .review-arrow, .focus-card small
) {
  color: #b8b4c3 !important;
}

:global(html[data-theme='dark'] .dashboard-page) :is(
  .attention-empty, .focus-tag
) {
  color: #8e8999 !important;
}

:global(html[data-theme='dark'] .dashboard-page) :is(
  .stat-label, .review-content strong, .attention-heading strong,
  .attention-item > strong
) {
  color: #f5f3f7 !important;
}

:global(html[data-theme='dark'] .dashboard-page) :is(
  .progress-track, .circle-bg
) {
  background: #403d4c;
  stroke: #403d4c;
}

:global(html[data-theme='dark'] .dashboard-page .recall-chart) {
  border-color: #4a465a;
}

:global(html[data-theme='dark'] .dashboard-page .attention-item),
:global(html[data-theme='dark'] .dashboard-page .mobile-stat-card) {
  border-color: #4a465a;
}

:global(html[data-theme='dark'] .dashboard-page .focus-tag) {
  background: #403d4c;
}

.dashboard-state {
  min-height: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--text-secondary);
  text-align: center;
}
.dashboard-state h2,
.dashboard-state p {
  margin: 0;
}
.dashboard-state h2 {
  color: var(--text);
}
.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #deddf2;
  border-top-color: #554dde;
  border-radius: 50%;
  animation: dashboard-spin 0.7s linear infinite;
}
.chart-empty {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: #777487;
  font-size: 14px;
}
.attention-empty {
  color: var(--text-secondary);
  font-size: 14px;
}
@keyframes dashboard-spin {
  to {
    transform: rotate(360deg);
  }
}

.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 12px;
}

h1,
h2,
p {
  margin: 0;
}

button {
  font-family: inherit;
}

.primary-icon {
  color: var(--primary);
}

/* =========================
   DESKTOP
========================= */

.desktop-dashboard {
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 37px 39px 60px;
}

.desktop-title-row {
  min-height: 171px;

  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.eyebrow {
  margin-bottom: 10px;

  color: #3730c9;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.desktop-title-row h1 {
  max-width: 650px;

  font-size: clamp(42px, 4vw, 55px);
  line-height: 1.07;
  letter-spacing: -1.8px;
  font-weight: 750;
}

.continue-button {
  min-width: 327px;
  height: 52px;
  margin-top: 90px;
  padding: 0 28px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  color: #ffffff;
  background: #5b54dc;

  border: none;
  border-radius: 8px;

  font-size: 14px;
  font-weight: 500;

  cursor: pointer;
}

.dashboard-top-grid {
  display: grid;
  grid-template-columns: minmax(350px, 414px) minmax(500px, 1fr);
  gap: 26px;
}

.today-plan {
  padding: 25px 26px;
}

.card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card h2 {
  font-size: 26px;
  line-height: 1.2;
  font-weight: 700;
}

.plan-stats {
  margin-top: 28px;

  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.plan-stat {
  height: 114px;
  padding: 17px;

  display: flex;
  flex-direction: column;

  background: #f7f7f9;

  border: 1px solid #dddde5;
  border-radius: 8px;
}

.plan-stat strong {
  color: #4a43ce;
  font-size: 48px;
  line-height: 1;
  font-weight: 650;
}

.plan-stat span {
  margin-top: 8px;
  color: #33333d;
  font-size: 14px;
}

.estimated-time {
  height: 70px;
  margin-top: 17px;
  padding: 0 18px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  background: #f7f7f9;

  border: 1px solid #dddde5;
  border-radius: 8px;
}

.estimated-label {
  display: flex;
  align-items: center;
  gap: 12px;

  color: #444451;
  font-size: 14px;
}

.estimated-time strong {
  font-size: 27px;
}

.daily-goal {
  margin-top: 35px;
}

.daily-goal-label {
  margin-bottom: 8px;

  display: flex;
  justify-content: space-between;

  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.progress-track {
  height: 8px;
  overflow: hidden;

  background: #e1e1e4;
  border-radius: 20px;
}

.progress-value {
  width: 60%;
  height: 100%;

  background: #443bd0;
  border-radius: inherit;
}

.start-session-button {
  width: 100%;
  height: 61px;
  margin-top: 56px;
  padding: 0 26px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;

  color: #ffffff;
  background: #5b55df;

  border: 0;
  border-radius: 8px;

  font-size: 15px;
  font-weight: 600;

  cursor: pointer;
}

.desktop-right-column {
  min-width: 0;

  display: flex;
  flex-direction: column;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 17px;
}

.stat-card {
  min-height: 114px;
  padding: 20px 18px;
}

.stat-label {
  display: flex;
  align-items: center;
  gap: 9px;

  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.7px;
}

.stat-value {
  margin-top: 9px;

  display: flex;
  align-items: baseline;
  gap: 6px;
}

.stat-value strong {
  font-size: 35px;
  line-height: 1;
}

.stat-value span {
  font-size: 13px;
}

.review-card {
  flex: 1;
  margin-top: 25px;
  padding: 25px 26px;
}

.review-list {
  margin-top: 22px;

  display: flex;
  flex-direction: column;
  gap: 13px;
}

.review-item {
  width: 100%;
  min-height: 77px;
  padding: 14px;

  display: flex;
  align-items: center;
  gap: 15px;

  color: #202126;
  background: #fafafa;

  border: 1px solid #d7d7e0;
  border-radius: 8px;

  text-align: left;
  cursor: pointer;
}

.review-item--danger {
  background: #fffafa;
  border-color: #ffc8c8;
}

.review-dot {
  width: 8px;
  height: 8px;
  flex: 0 0 auto;

  border-radius: 50%;
}

.review-dot--danger {
  background: #cc2228;
}

.review-dot--primary {
  background: #4940cf;
}

.review-dot--muted {
  background: #ccccda;
}

.review-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.review-content strong {
  font-size: 14px;
}

.review-content small {
  color: #3e3e4d;
  font-size: 12px;
}

.review-item--danger small {
  color: #c10009;
}

.dashboard-bottom-grid {
  margin-top: 26px;

  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 26px;
}

.recall-card,
.attention-card {
  min-height: 397px;
  padding: 27px;
}

.section-subtitle {
  display: block;
  margin-top: 4px;

  color: #373745;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.6px;
}

.topic-filter {
  padding: 7px 11px;

  display: flex;
  align-items: center;
  gap: 9px;

  background: #f5f5f7;
  border: 0;
  border-radius: 7px;
}

.filter-chevron {
  transform: rotate(90deg);
}

.chart {
  position: relative;
  height: 280px;
  margin-top: 18px;
}

.recall-chart {
  display: flex;
  align-items: end;
  gap: 4px;
  height: 100%;
  padding: 12px 2px 0;
  border-bottom: 1px solid #e5e3ed;
}

.recall-chart-day {
  display: flex;
  flex: 1;
  height: 100%;
  align-items: end;
  min-width: 0;
}

.recall-chart-bar {
  width: 100%;
  min-height: 2px;
  border-radius: 4px 4px 0 0;
  background: #5a52d8;
}

.chart-svg {
  position: absolute;
  inset: 0;

  width: 100%;
  height: 100%;
}

.chart-line {
  fill: none;

  stroke: #443bd0;
  stroke-width: 6;
  stroke-linecap: round;
}

.chart-fill {
  fill: url(#chartFill);
}

.chart-label {
  position: absolute;
  left: -17px;

  z-index: 2;

  color: #a1a1af;
  font-size: 12px;
}

.chart-label-100 {
  top: 12px;
}

.chart-label-75 {
  top: 105px;
}

.chart-label-50 {
  top: 197px;
}

.attention-item {
  margin-top: 24px;
  padding: 18px;

  border: 1px solid #ccccdc;
  border-radius: 8px;
}

.attention-heading {
  display: flex;
  justify-content: space-between;
}

.attention-heading strong,
.attention-item > strong {
  font-size: 14px;
}

.attention-danger {
  margin-top: 4px;

  display: flex;
  align-items: center;
  gap: 4px;

  color: #c50008;
  font-size: 12px;
  font-weight: 500;
}

.score-circle {
  width: 43px;
  height: 43px;

  display: grid;
  place-items: center;

  color: #bf0009;

  border: 2px solid #cc0010;
  border-radius: 50%;

  font-size: 16px;
  font-weight: 600;
}

.attention-actions {
  margin-top: 14px;

  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.attention-actions button {
  height: 43px;

  background: #efeff0;

  border: 1px solid #dbdbe2;
  border-radius: 4px;

  cursor: pointer;
}

.attention-actions .outline-button {
  color: #322acb;
  background: var(--surface-card);
  border-color: #c7c7d9;
}

/* Hide mobile by default */

.mobile-dashboard {
  display: none;
}

/* =========================
   MOBILE
========================= */

@media (max-width: 1023px) {
  .desktop-dashboard {
    display: none;
  }

  .mobile-dashboard {
    display: block;
    padding: 17px 16px 30px;
  }

  .mobile-welcome {
    margin-bottom: 48px;
  }

  .mobile-date {
    margin-bottom: 6px;

    color: #535363;
    font-size: 14px;
    letter-spacing: 0.7px;
  }

  .mobile-welcome h1 {
    font-size: 24px;
    line-height: 1.2;
    font-weight: 700;
  }

  /* Study card */

  .study-session-card {
    position: relative;

    min-height: 176px;
    overflow: hidden;

    color: #ffffff;
    background: #4941d1;

    border-radius: 12px;

    box-shadow:
      0 6px 11px rgba(45, 39, 150, 0.16),
      0 12px 25px rgba(45, 39, 150, 0.08);
  }

  .study-pattern {
    position: absolute;
    inset: 0;

    opacity: 0.2;

    background-image: radial-gradient(circle, rgba(255, 255, 255, 0.8) 1px, transparent 1px);

    background-size: 20px 20px;
  }

  .study-card-content {
    position: relative;
    z-index: 2;

    min-height: 176px;
    padding: 25px 24px 22px;

    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .study-card-content h2 {
    font-size: 23px;
  }

  .study-card-content p {
    margin-top: 10px;
    font-size: 15px;
  }

  .study-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .study-time {
    height: 30px;
    padding: 0 13px;

    display: flex;
    align-items: center;
    gap: 7px;

    background: rgba(255, 255, 255, 0.15);

    border-radius: 18px;

    font-size: 13px;
    font-weight: 600;
  }

  .study-play-button {
    width: 44px;
    height: 44px;

    display: grid;
    place-items: center;

    color: #4941d1;
    background: var(--surface-card);

    border: 0;
    border-radius: 50%;
  }

  /* Stats */

  .mobile-stats {
    margin-top: 48px;

    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .mobile-stat-card {
    height: 150px;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    background: var(--surface-card);

    border: 1px solid #cdcede;
    border-radius: 11px;
  }

  .circle-progress {
    position: relative;

    width: 74px;
    height: 74px;

    display: grid;
    place-items: center;
  }

  .circle-progress svg {
    position: absolute;
    inset: 0;

    width: 100%;
    height: 100%;

    transform: rotate(-90deg);
  }

  .circle-bg,
  .circle-value {
    fill: none;
    stroke-width: 7;
  }

  .circle-bg {
    stroke: #e5e5e5;
  }

  .circle-value {
    stroke: #4941d1;
    stroke-linecap: butt;

    stroke-dasharray: 182;
    stroke-dashoffset: 73;
  }

  .circle-progress span {
    position: relative;
    z-index: 2;

    font-size: 14px;
    font-weight: 700;
  }

  .mobile-stat-card small {
    margin-top: 6px;

    color: #525263;
    font-size: 12px;
    font-weight: 600;
  }

  .streak-circle {
    width: 57px;
    height: 57px;
    margin-bottom: 10px;

    display: grid;
    place-items: center;

    background: #ededee;
    color: var(--primary);
    border-radius: 50%;
  }

  .streak-card strong {
    font-size: 22px;
    line-height: 1.1;
  }

  .streak-card small {
    margin-top: 3px;
  }

  /* Sections */

  .mobile-section {
    margin-top: 50px;
  }

  .mobile-section-heading {
    margin-bottom: 16px;

    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .mobile-section h2 {
    font-size: 23px;
  }

  .mobile-section-heading button {
    padding: 4px;

    color: #443bd0;
    background: transparent;
    border: 0;

    font-size: 13px;
  }

  .mobile-review-list {
    display: flex;
    flex-direction: column;
    gap: 9px;
  }

  .mobile-review-item {
    min-height: 74px;
    padding: 12px 15px;

    display: flex;
    align-items: center;
    gap: 12px;

    color: #202126;
    background: var(--surface-card);

    border: 1px solid #ccccdc;
    border-radius: 8px;

    text-align: left;
  }

  .mobile-review-item .review-content {
    min-width: 0;
    flex: 1;
  }

  .mobile-review-item .review-content strong {
    display: -webkit-box;
    overflow: hidden;
    font-size: 14px;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .mobile-review-item .review-content small {
    font-size: 11px;
    font-weight: 600;
  }

  .review-arrow {
    color: #858594;
  }

  /* Focus areas */

  .focus-section {
    margin-top: 49px;
  }

  .focus-section > h2 {
    margin-bottom: 18px;
  }

  .focus-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .focus-card {
    min-height: 142px;
    padding: 17px;

    display: flex;
    flex-direction: column;
    align-items: flex-start;

    background: #f7f7f8;
    border-radius: 8px;
  }

  .focus-tag {
    margin-bottom: 10px;
    padding: 5px 8px;

    color: #60606c;
    background: #dadde0;

    border-radius: 3px;

    font-size: 11px;
    font-weight: 700;
  }

  .focus-tag--danger {
    color: #c81820;
    background: #ffe3e4;
  }

  .focus-card strong {
    overflow-wrap: anywhere;
    font-size: 13px;
    font-weight: 500;
  }

  .focus-card small {
    margin-top: 7px;

    color: #555563;
    font-size: 11px;
    font-weight: 600;
  }

  .focus-action {
    margin-top: auto;
    padding: 6px 0 0;
    color: #443bd0;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
  }
}

/* Small mobile */

@media (max-width: 380px) {
  .mobile-dashboard {
    padding-inline: 12px;
  }

  .mobile-stats {
    gap: 10px;
  }

  .focus-card {
    padding: 13px;
  }

  .focus-grid {
    grid-template-columns: 1fr;
  }
}
</style>
